// packages/shared/src/utils/specialtyUtils.ts
// Utility functions for clinical specialty management

import type { ClinicalSpecialty } from '../types/models/users';

/** Returns the org's specialties, defaulting to ['physical_therapy'] if not set */
export function getOrgSpecialties(org: { specialties?: ClinicalSpecialty[] }): ClinicalSpecialty[] {
  return org?.specialties?.length ? org.specialties : ['physical_therapy'];
}

/** Whether the org supports more than one clinical specialty */
export function isMultiSpecialty(org: { specialties?: ClinicalSpecialty[] }): boolean {
  return getOrgSpecialties(org).length > 1;
}

/** Whether the specialty picker should be shown (alias for isMultiSpecialty) */
export function shouldShowSpecialtyPicker(org: { specialties?: ClinicalSpecialty[] }): boolean {
  return isMultiSpecialty(org);
}

/** Human-readable label for a ClinicalSpecialty value */
export function getSpecialtyLabel(specialty: ClinicalSpecialty): string {
  const labels: Record<ClinicalSpecialty, string> = {
    physical_therapy: 'Physical Therapy',
    occupational_therapy: 'Occupational Therapy',
    speech_language_pathology: 'Speech-Language Pathology',
    primary_care: 'Primary Care',
    orthopedics: 'Orthopedics',
    neurology: 'Neurology',
    cardiology: 'Cardiology',
    pain_management: 'Pain Management',
    psychiatry: 'Psychiatry',
    chiropractic: 'Chiropractic',
    sports_medicine: 'Sports Medicine',
    massage_therapy: 'Massage Therapy',
    longevity: 'Longevity',
    functional_medicine: 'Functional Medicine',
    wellness: 'Wellness',
    general: 'General',
  };
  return labels[specialty] || specialty;
}

/** Practitioner title per specialty, for patient-facing wording. Specialties without one keep "provider". */
const SPECIALTY_PROVIDER_TITLES: Partial<Record<ClinicalSpecialty, string>> = {
  physical_therapy: 'physical therapist',
  occupational_therapy: 'occupational therapist',
  speech_language_pathology: 'speech-language pathologist',
  chiropractic: 'chiropractor',
  massage_therapy: 'massage therapist',
  neurology: 'neurologist',
  cardiology: 'cardiologist',
  psychiatry: 'psychiatrist',
};

/** Practitioner title for a specialty ("physical therapist"), or null when the specialty has none */
export function getSpecialtyProviderTitle(specialty: ClinicalSpecialty | string | undefined | null): string | null {
  return (specialty && SPECIALTY_PROVIDER_TITLES[specialty as ClinicalSpecialty]) || null;
}

/**
 * Replaces the whole word "provider" in patient-facing text with the specialty's
 * practitioner title ("your provider" -> "your physical therapist"). Keeps a
 * leading capital and plural / possessive endings, and fixes a preceding
 * "a" / "an". Text is returned unchanged when the specialty has no title.
 */
export function withSpecialtyProviderTitle(text: string, specialty: ClinicalSpecialty | string | undefined | null): string {
  const title = getSpecialtyProviderTitle(specialty);
  if (!title || !text) return text;
  const article = /^[aeiou]/i.test(title) ? 'an' : 'a';
  return text.replace(/\b(?:(an?)(\s+))?(p)rovider(s?)\b/gi, (_match, oldArticle, space, firstLetter, plural) => {
    const word = (firstLetter === 'P' ? title.charAt(0).toUpperCase() + title.slice(1) : title) + plural;
    if (!oldArticle) return word;
    const nextArticle = oldArticle.charAt(0) === 'A' ? article.charAt(0).toUpperCase() + article.slice(1) : article;
    return nextArticle + space + word;
  });
}

/** Therapy specialties whose cases carry a prescription (script dates + prescribed visits) */
export const THERAPY_SPECIALTIES: ClinicalSpecialty[] = [
  'physical_therapy',
  'occupational_therapy',
  'speech_language_pathology',
];

/** Whether a specialty is a therapy discipline (PT/OT/SLP) */
export function isTherapySpecialty(specialty: ClinicalSpecialty | string | undefined | null): boolean {
  return !!specialty && THERAPY_SPECIALTIES.includes(specialty as ClinicalSpecialty);
}

/** All available clinical specialties for selection UIs */
export const ALL_CLINICAL_SPECIALTIES: ClinicalSpecialty[] = [
  'physical_therapy',
  'occupational_therapy',
  'speech_language_pathology',
  'primary_care',
  'orthopedics',
  'neurology',
  'cardiology',
  'pain_management',
  'psychiatry',
  'chiropractic',
  'sports_medicine',
  'massage_therapy',
  'longevity',
  'functional_medicine',
  'wellness',
  'general',
];

/** Maps clinical specialties to ClaimMD eligibility service_code values */
export const SPECIALTY_SERVICE_CODES: Partial<Record<ClinicalSpecialty, string>> = {
  physical_therapy: 'PT',
  occupational_therapy: 'OT',
  speech_language_pathology: 'SP',
  chiropractic: '33',
};

/** Returns the ClaimMD service codes to check for an org's specialties */
export function getOrgServiceCodes(org: { specialties?: ClinicalSpecialty[] }): { specialty: ClinicalSpecialty; serviceCode: string }[] {
  return getOrgSpecialties(org)
    .filter(s => s in SPECIALTY_SERVICE_CODES)
    .map(s => ({ specialty: s, serviceCode: SPECIALTY_SERVICE_CODES[s]! }));
}

/** Human-readable labels for ClaimMD service codes */
export const SERVICE_CODE_LABELS: Record<string, string> = {
  '30': 'Health Benefit Plan',
  'PT': 'Physical Therapy',
  'OT': 'Occupational Therapy',
  'SP': 'Speech-Language Pathology',
  '33': 'Chiropractic',
};
